import React from "react";
import { TFunction } from "i18next";
import { toast } from "react-toastify";
import { CheckboxIcon } from "@aureskonnect/react-ui";
import { uuid } from "uuidv4";

import { CESAR_LOYALTY, numberMatchWord } from "@constants/index";
import { getData } from "@helpers/general";
import ErrorToast from "@components/Common/ErrorTost";
import { clearData } from "@components/Common/MultiCustomCreatableSelection/store";
import { clearMultiCustomSelectionData } from "@components/Common/MultiCustomSelect/store";

import { store } from "../../../../../redux/store";
import {
    setNumberActive,
    setIsEdited,
    setIsArchived,
    setEdit,
    setKey,
} from "../../../../../redux/slices/fidelitySlice";

import { FormValues, FormFlags, FormStateApi } from "./types";

type FetchGlobal = (url: string, options: RequestInit) => Promise<any>;

const apiUrl = () =>
    `${process.env.REACT_APP_API_V2_URL}/settings/general/fidelity`;

const successIcon = <CheckboxIcon height={25} width={25} fill="white" />;

function successToast(message: string) {
    toast.success(message, {
        position: toast.POSITION.TOP_CENTER,
        theme: "colored",
        icon: successIcon,
    } as any);
}

function errorToast(message: string) {
    toast.error(message, {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
        theme: "colored",
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
    });
}

function warningToast(message: string) {
    toast.warning(message, {
        position: toast.POSITION.TOP_CENTER,
        theme: "colored",
        icon: successIcon,
    } as any);
}

export function isFormValidForType(
    values: FormValues,
    oneShop: boolean,
    t: TFunction
): boolean {
    const {
        choiceFidelity,
        auresKonnectGroup,
        numberFidelityPoints,
        login,
        password,
        user,
        dashboardLink,
        apiKey,
    } = values;

    if (choiceFidelity.startsWith(t("Fidelity Softavera"))) {
        return (
            auresKonnectGroup !== "" &&
            !!numberFidelityPoints.match(numberMatchWord)
        );
    }
    if (choiceFidelity === t("Fidelity Synapsy")) {
        return !oneShop || (login !== "" && password !== "");
    }
    if (choiceFidelity === t("Fidelity Zerosix")) {
        return (
            !oneShop ||
            (user !== "" &&
                password !== "" &&
                dashboardLink !== "" &&
                apiKey !== "")
        );
    }
    if (choiceFidelity === t("Godia")) return true;
    return false;
}

type ValidateParams = {
    state: FormStateApi;
    isEdited: boolean;
    isExist: boolean;
    isFidelityNumberInValid: boolean;
    oneShop: boolean;
    dataFidelity: any;
    isModalOpened: boolean;
    setIsModalOpened: (v: boolean) => void;
    onAdd: () => void;
    onEdit: () => void;
    t: TFunction;
};

export function handleValidate(params: ValidateParams) {
    const {
        state,
        isEdited,
        isExist,
        isFidelityNumberInValid,
        oneShop,
        dataFidelity,
        isModalOpened,
        setIsModalOpened,
        onAdd,
        onEdit,
        t,
    } = params;
    const { values, flags, setError } = state;

    const hasSaleActive = values.sortedVisibility.some(
        (v: any) => v.state === true
    );

    if (flags.isCentralizationActive && !flags.isVisibiliteActive) {
        warningToast(
            t(
                "Attention, for loyalty centralization, please activate the loyalty configuration"
            )
        );
        return;
    }
    if (!hasSaleActive && flags.isCentralizationActive) {
        warningToast(t("Attention, please activate at least one sales mode"));
        return;
    }

    if (values.designation === "") setError("designation", true);
    if (values.choiceFidelity === "") setError("choiceFidelity", true);
    if (values.login === "") setError("login", true);
    if (values.password === "") setError("password", true);
    if (values.user === "") setError("user", true);
    if (values.numberFidelityPoints === "")
        setError("numberFidelityPoints", true);
    if (values.dashboardLink === "") setError("dashboardLink", true);
    if (values.apiKey === "") setError("apiKey", true);
    if (values.auresKonnectGroup === "") setError("group", true);

    const isValid = isFormValidForType(values, oneShop, t);

    if (dataFidelity.Id !== undefined) {
        if (!isEdited && dataFidelity[t("Remark")] === values.remark) {
            setIsModalOpened(!isModalOpened);
            return;
        }
        if (
            values.choiceFidelity !== "" &&
            !isExist &&
            !isFidelityNumberInValid &&
            isValid
        ) {
            onEdit();
        }
    } else if (
        values.designation !== "" &&
        values.choiceFidelity !== "" &&
        !isExist &&
        !isFidelityNumberInValid &&
        isValid
    ) {
        onAdd();
    }

    store.dispatch(setEdit(uuid()));
}

function buildPayload(
    values: FormValues,
    flags: FormFlags,
    shopId: string,
    fidelityChoiceIndex: number
) {
    return {
        designation: values.designation,
        type: values.choiceFidelity,
        number_fidelity_points: values.numberFidelityPoints,
        money_correspondence: values.moneyCorrespondence,
        authentication_mode: values.authenticationMode.join(","),
        sortedVisibility: values.sortedVisibility,
        Login: values.login,
        password: values.password,
        note: values.remark,
        isActive: false as boolean,
        isArchived: false as boolean | string,
        shopId,
        dashboardLink: values.dashboardLink,
        user: values.user,
        apiKey: values.apiKey,
        auresKonnectGroup: values.auresKonnectGroup,
        isCentralizationActive: flags.isCentralizationActive,
        isVisibiliteActive: flags.isVisibiliteActive,
        fidelityChoiceIndex,
    };
}

type AddParams = {
    values: FormValues;
    flags: FormFlags;
    fidelityChoiceIndex: number;
    shopId: string;
    userID: number;
    franchiseID: string;
    operatorID: string;
    uuidUser: string | null;
    oneShop: boolean;
    numberActive: number;
    fidelityData: any;
    fidelityFranchise: any;
    isFranchiseExist: boolean;
    isFranchiseExistAndArchived: boolean;
    fetchGlobal: FetchGlobal;
    t: TFunction;
    setIsValidateButtonEnabled: (v: boolean) => void;
    setIsModalModifiedOpened: (v: boolean) => void;
    setIsAddFidelityClicked: (v: boolean) => void;
    isAddFidelityClicked: boolean;
    setDataFidelity: (rows: FidelityRow[]) => void;
    setLocalFilterActive: (v: boolean) => void;
    setDataIsUpdated: (v: boolean) => void;
    setFidelityChoiceIndex: (idx: number) => void;
    resetForm: () => void;
};

export async function addFidelity(params: AddParams) {
    const {
        values,
        flags,
        fidelityChoiceIndex,
        shopId,
        userID,
        franchiseID,
        operatorID,
        uuidUser,
        oneShop,
        numberActive,
        fidelityData,
        fidelityFranchise,
        isFranchiseExist,
        isFranchiseExistAndArchived,
        fetchGlobal,
        t,
        setIsValidateButtonEnabled,
        setIsModalModifiedOpened,
        setIsAddFidelityClicked,
        isAddFidelityClicked,
        setDataFidelity,
        setLocalFilterActive,
        setDataIsUpdated,
        setFidelityChoiceIndex,
        resetForm,
    } = params;

    const body = {
        userId: userID,
        shopId: oneShop ? [shopId, "0"] : [shopId],
        franchiseId: franchiseID,
        operatorId: operatorID,
        uuidUser,
        adding: true,
        data: {
            [uuid()]: buildPayload(values, flags, shopId, fidelityChoiceIndex),
        },
    };

    const franchiseData = oneShop
        ? fidelityFranchise.othersData?.dataFranchise?.data
        : fidelityFranchise;

    if (fidelityData.data.length > 0 && oneShop) {
        ErrorToast(
            t("Attention, a shop cannot have more than one setting fidelity")
        );
        setIsValidateButtonEnabled(true);
        return;
    }
    if (
        !isFranchiseExist &&
        !isFranchiseExistAndArchived &&
        oneShop &&
        franchiseData?.length !== 0
    ) {
        ErrorToast(
            t(
                "Attention, a shop cannot have a loyalty system other than the system assigned to the franchise."
            )
        );
        return;
    }
    if (isFranchiseExistAndArchived && oneShop) {
        setIsModalModifiedOpened(true);
        store.dispatch(setIsArchived(true));
        setIsValidateButtonEnabled(true);
        return;
    }
    if (isFranchiseExist && oneShop) {
        setIsModalModifiedOpened(true);
        store.dispatch(setIsArchived(false));
        setIsValidateButtonEnabled(true);
        return;
    }

    setIsValidateButtonEnabled(false);

    try {
        const result: any = await fetchGlobal(apiUrl(), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
        if (result.error) {
            setDataIsUpdated(true);
            throw new Error("error");
        }
        await getData(franchiseID, uuidUser as string, shopId, fetchGlobal);
        store.dispatch(setKey(uuid()));
        successToast(t("The fidelity setting has been successfully added"));
        setDataFidelity([]);
        store.dispatch(setIsEdited(false));
        setLocalFilterActive(false);
        store.dispatch(setNumberActive(numberActive + 1));
        resetForm();
        clearData();
        clearMultiCustomSelectionData();
        setFidelityChoiceIndex(0);
        setIsAddFidelityClicked(!isAddFidelityClicked);
    } catch (e) {
        errorToast(t("There's an error"));
    } finally {
        setIsValidateButtonEnabled(true);
    }
}

function findAssociatedFidelities(row: any, shopsFidelityData: any): any[] {
    const result: any[] = [];
    shopsFidelityData.data?.forEach((element: any) => {
        element.subRows?.forEach((el: any) => {
            if (el.Id === row.Id) {
                result.push(JSON.parse(JSON.stringify(el)));
            }
        });
    });
    return [...new Set(result)];
}

type EditParams = {
    values: FormValues;
    flags: FormFlags;
    fidelityChoiceIndex: number;
    shopId: string;
    userID: number;
    franchiseID: string;
    operatorID: string;
    uuidUser: string | null;
    oneShop: boolean;
    dataFidelity: any;
    shopsFidelityData: any;
    fetchGlobal: FetchGlobal;
    t: TFunction;
    setIsValidateButtonEnabled: (v: boolean) => void;
    setIsModalOpen: (v: boolean) => void;
    setIsAddFidelityClicked: (v: boolean) => void;
    // Receives the edit request body (mirrored in `body: JSON.stringify(dataConfig)`).
    // Stored in the parent's `useState<any>` (FidelityFormWrapper/index.tsx:134) and
    // currently re-passed as `dataFidelity` to a child — that re-purposing is itself
    // a separate cleanup task; we model only what is *sent* here.
    setDataEdited: (v: EditDataConfig) => void;
};

// `data` value shape is tied to `buildPayload`'s actual return, not to
// the global `FidelityPayload` (which declares `devise`/`pays` that this
// builder doesn't produce — separate drift to address). Using ReturnType
// keeps the two locked together without manual transcription.
type EditDataConfig = {
    userId: number;
    shopId: string[];
    franchiseId: string;
    operatorId: string;
    uuidUser: string | null;
    data: Record<string, ReturnType<typeof buildPayload>>;
};

export async function editFidelity(params: EditParams) {
    const {
        values,
        flags,
        fidelityChoiceIndex,
        shopId,
        userID,
        franchiseID,
        operatorID,
        uuidUser,
        oneShop,
        dataFidelity,
        shopsFidelityData,
        fetchGlobal,
        t,
        setIsValidateButtonEnabled,
        setIsModalOpen,
        setIsAddFidelityClicked,
        setDataEdited,
    } = params;

    toast.dismiss();
    const associated =
        !oneShop && dataFidelity.fidelityChoiceIndex !== CESAR_LOYALTY
            ? findAssociatedFidelities(dataFidelity, shopsFidelityData)
            : [];

    const payload = buildPayload(values, flags, shopId, fidelityChoiceIndex);
    payload.isActive = dataFidelity.isActive;
    payload.isArchived =
        dataFidelity.fidelityChoiceIndex !== CESAR_LOYALTY
            ? false
            : JSON.stringify(dataFidelity.isArchived);

    const dataConfig = {
        userId: userID,
        shopId: [shopId],
        franchiseId: franchiseID,
        operatorId: operatorID,
        uuidUser,
        data: { [dataFidelity.Id]: payload },
    };
    setDataEdited(dataConfig);
    setIsValidateButtonEnabled(false);

    try {
        const result: any = await fetchGlobal(apiUrl(), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(dataConfig),
        });
        if (result.error) throw new Error("error");

        successToast(
            t(
                associated.length === 0
                    ? "The fidelity setting has been successfully modified"
                    : "The fidelity of franchise  was successfully changed"
            )
        );
        if (associated.length > 0) {
            setIsModalOpen(true);
        } else {
            setIsAddFidelityClicked(false);
        }
        store.dispatch(setIsEdited(false));
        store.dispatch(setKey(uuid()));
    } catch (e) {
        errorToast(t("There's an error"));
    } finally {
        setIsValidateButtonEnabled(true);
    }
}
